import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Premium label-on-top input card used across the venue onboarding flow.
 *
 * - White card with soft purple border, lifts on focus.
 * - Persistent floating label that highlights when focused.
 * - Optional `icon` slot (lucide component) on the right.
 * - Works as a controlled component.
 */
export function PremiumField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  testId,
  icon: Icon,
  inputMode,
  autoComplete,
  hint,
  disabled = false,
  required = false,
  maxLength,
  multiline = false,
  rows = 3,
  onFocus,
  onBlur,
  onKeyDown,
}) {
  const [focused, setFocused] = useState(false);

  const baseInputStyle = {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: "'Inria Serif', serif",
    fontSize: 16,
    fontWeight: 500,
    color: 'var(--text)',
    padding: 0,
    resize: multiline ? 'vertical' : 'none',
    minHeight: multiline ? rows * 22 : undefined,
  };

  return (
    <div style={{ width: '100%', marginBottom: 14 }}>
      <label
        style={{
          display: 'block',
          fontFamily: "'Inria Serif', serif",
          fontSize: 12.5,
          fontWeight: 700,
          color: 'var(--accent-purple)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          marginBottom: 6,
          marginLeft: 4,
          transition: 'color 200ms ease',
        }}
      >
        {label}
        {required ? <span style={{ color: '#E53935', marginLeft: 4 }}>*</span> : null}
      </label>
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          padding: '14px 16px',
          border: `1.5px solid ${focused ? 'var(--accent-purple)' : 'var(--card-border)'}`,
          boxShadow: focused
            ? '0 0 0 4px var(--accent-surface-soft), 0 6px 18px var(--inner-shadow)'
            : 'var(--premium-shadow-nested)',
          display: 'flex',
          alignItems: multiline ? 'flex-start' : 'center',
          gap: 12,
          transition: 'border-color 200ms ease, box-shadow 200ms ease',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        {multiline ? (
          <textarea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            data-testid={testId}
            rows={rows}
            disabled={disabled}
            maxLength={maxLength}
            onFocus={(event) => { setFocused(true); onFocus?.(event); }}
            onBlur={(event) => { setFocused(false); onBlur?.(event); }}
            style={baseInputStyle}
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            data-testid={testId}
            inputMode={inputMode}
            autoComplete={autoComplete}
            disabled={disabled}
            maxLength={maxLength}
            onFocus={(event) => { setFocused(true); onFocus?.(event); }}
            onBlur={(event) => { setFocused(false); onBlur?.(event); }}
            onKeyDown={onKeyDown}
            style={baseInputStyle}
          />
        )}
        {Icon ? (
          <Icon size={18} color={focused ? 'var(--accent-purple)' : 'var(--text-3)'} strokeWidth={2} />
        ) : null}
      </div>
      {hint ? (
        <p
          style={{
            margin: '6px 4px 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 12.5,
            color: 'var(--text-3)',
            fontWeight: 500,
          }}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Selector field that opens a sheet/dropdown — used for country picker etc.
 * Visually matches `PremiumField`.
 */
export function PremiumSelectField({
  label,
  value,
  placeholder,
  onClick,
  testId,
  required = false,
  hint,
}) {
  return (
    <div style={{ width: '100%', marginBottom: 14 }}>
      <label
        style={{
          display: 'block',
          fontFamily: "'Inria Serif', serif",
          fontSize: 12.5,
          fontWeight: 700,
          color: 'var(--accent-purple)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          marginBottom: 6,
          marginLeft: 4,
        }}
      >
        {label}
        {required ? <span style={{ color: '#E53935', marginLeft: 4 }}>*</span> : null}
      </label>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        style={{
          width: '100%',
          background: 'var(--surface)',
          borderRadius: 16,
          padding: '14px 16px',
          border: '1.5px solid var(--card-border)',
          boxShadow: 'var(--premium-shadow-nested)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          cursor: 'pointer',
          fontFamily: "'Inria Serif', serif",
          fontSize: 16,
          fontWeight: value ? 600 : 500,
          color: value ? 'var(--text)' : 'var(--text-3)',
          textAlign: 'left',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || placeholder}
        </span>
        <ChevronDown size={18} color="#A39BC6" strokeWidth={2} />
      </button>
      {hint ? (
        <p
          style={{
            margin: '6px 4px 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 12.5,
            color: 'var(--text-3)',
            fontWeight: 500,
          }}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}
